import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  IAgency,
  IAgencyCodeType,
  IAgencyType,
} from '@core/interfaces/iagency';
import { IVendor } from '@core/interfaces/ivendor';
import { AgencyService } from '@core/services/agency.service';
import { IState } from '@core/interfaces/istate';
import { ICounty } from '@core/interfaces/icounty';
import { Observable, Subscription, take } from 'rxjs';
import { ToastService } from '@core/services/toast.service';
import { SelectType } from '@core/enums/select';
import { HeadStart } from '@core/enums/head-start';
import { IRegionType } from '@core/interfaces/iregion-type';
import { StateService } from '@core/services/state.service';
import { IHeadStartOption } from '@core/interfaces/ihead-start';
import { StatePreschool } from '@core/enums/state-preschool';
import { States } from '@core/enums/states';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@core/enums/permissions';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-add-edit-agency',
    templateUrl: './add-edit-agency.component.html',
    styleUrls: ['./add-edit-agency.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddEditAgencyComponent implements OnInit {
  private subscriptions = new Subscription();
  isProcessing = false;
  agencyForm: FormGroup | any;
  selectedState?: IState;
  selectedCodeType?: IAgencyCodeType;
  selectedType?: IAgencyType;
  selectedCounty?: ICounty;
  selectedVendor?: IVendor[];
  selectedRegion?: number[];
  title = '';
  isAgencyDelegate = false;
  states = States;
  isEditMode = false;
  canEditNameAndVendor = true;
  canEditAgencyStatus = false;

  public get HeadStart() {
    return HeadStart;
  }
  public get StatePreschool() {
    return StatePreschool;
  }

  getSelectData$: Observable<any> | undefined;

  public get selectType() {
    return SelectType;
  }
  get agencyName() {
    return this.agencyForm.get('agencyName');
  }
  get cdsCode() {
    return this.agencyForm.get('cdsCode');
  }
  get codeType() {
    return this.agencyForm.get('codeType');
  }
  get stateId() {
    return this.agencyForm.get('stateId');
  }
  get typeId() {
    return this.agencyForm.get('typeId');
  }
  get footer() {
    return this.agencyForm.get('footer');
  }
  get agencyAddress() {
    return this.agencyForm.get('agencyAddress');
  }
  get agencyCity() {
    return this.agencyForm.get('agencyCity');
  }
  get agencyZipCode() {
    return this.agencyForm.get('agencyZipCode');
  }
  get countyId() {
    return this.agencyForm.get('countyId');
  }

  get headStartId() {
    return this.agencyForm.get('headStartId');
  }
  get granteeAgencyId() {
    return this.agencyForm.get('granteeAgencyId');
  }

  get vendorIds() {
    return this.agencyForm.get('vendorIds');
  }
  get regionIds() {
    return this.agencyForm.get('regionIds');
  }

  get statePreschoolId() {
    return this.agencyForm.get('statePreschoolId');
  }
  get contractorAgencyId() {
    return this.agencyForm.get('contractorAgencyId');
  }
  get isTestAgency() {
    return this.agencyForm.get('isTestAgency');
  }
  get isActive() {
    return this.agencyForm.get('isActive');
  }
  get vendorNumber() {
    return this.agencyForm.get('vendorNumber');
  }

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddEditAgencyComponent>,
    private agencyService: AgencyService,
    private toast: ToastService,
    public stateService: StateService,
    private permissionService: PermissionService,
    @Inject(MAT_DIALOG_DATA) public editData: any,
    private translocoService: TranslocoService
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.title = this.translocoService.translate('configuration.agencies.addNew');
    this.initializeForms();
  }

  initializeForms(): void {
    this.agencyForm = this.fb.group({
      agencyName: [null, [Validators.required, Validators.maxLength(100)]],
      cdsCode: [null, [Validators.required, Validators.maxLength(25)]],
      codeType: [null, [Validators.required]],
      stateId: [null, [Validators.required]],
      typeId: [null, [Validators.required]],
      agencyAddress: [null, [Validators.required, Validators.maxLength(100)]],
      agencyCity: [null, [Validators.required, Validators.maxLength(25)]],
      agencyZipCode: [null, [Validators.required, Validators.maxLength(10)]],
      countyId: [
        null,
        [Validators.maxLength(50), Validators.pattern(/^-?(0|[1-9]\d*)?$/)],
      ],
      footer: [null, Validators.maxLength(250)],
      headStartId: [null],
      granteeAgencyId: [null],
      vendorIds: [null, Validators.required],
      regionIds: [null],
      statePreschoolId: [null],
      contractorAgencyId: [null],
      isTestAgency: [false],
      isActive: [false],
      vendorNumber: [
        null,
        [Validators.maxLength(19), Validators.pattern(/^[A-Za-z0-9]*$/)],
      ],
    });

    this.headStartId.valueChanges.subscribe((val: any) => {
      if (val == HeadStart.HeadStartDelegate) {
        this.granteeAgencyId.setValidators([Validators.required]);
      } else {
        this.granteeAgencyId.clearValidators();
      }
      this.granteeAgencyId.updateValueAndValidity();
    });

    this.statePreschoolId.valueChanges.subscribe((val: any) => {
      if (val == StatePreschool.StatePreschoolSubcontractor) {
        this.contractorAgencyId.setValidators([Validators.required]);
      } else {
        this.contractorAgencyId.clearValidators();
      }
      this.contractorAgencyId.updateValueAndValidity();
    });

    this.subscriptions.add(
      this.stateId.valueChanges.subscribe((stateId: number) => {
        if (stateId == this.states.california) {
          this.countyId.setValidators([Validators.required]);
        } else {
          this.countyId.clearValidators();
        }
        this.countyId.updateValueAndValidity();

        const vendorNumberValidators = [
          Validators.maxLength(19),
          Validators.pattern(/^[A-Za-z0-9]*$/),
        ];
        if (stateId == this.states.california) {
          vendorNumberValidators.push(Validators.required);
        }
        this.vendorNumber.setValidators(vendorNumberValidators);
        this.vendorNumber.updateValueAndValidity();
      })
    );

    if (this.editData && this.editData.data && this.editData.data.id) {
      const edit = this.editData.data;
      this.isEditMode = true;

      this.title =
        edit.headStartId == HeadStart.HeadStartGrantee
          ? this.translocoService.translate('configuration.agencies.addEdit.editGranteeTitle')
          : this.translocoService.translate('configuration.agencies.addEdit.editTitle');

      this.agencyForm.patchValue({
        ...edit,
        agencyName: edit.agencyName,
        countyID: edit.countyID,
        codeType: edit.codeType,
        stateId: edit.stateId,
        typeId: edit.siteTypeId,
        headStartId: edit.headStartId,
        granteeAgencyId: edit.granteeAgencyId,
        statePreschoolId: edit.statePreschoolId,
        contractorAgencyId: edit.contractorAgencyId,
        vendorIds: edit.vendorIds?.split(',').map(Number),
        regionIds: edit.regionIds?.split(',').map(Number),
        isActive: edit.isActive ?? false,
        vendorNumber: edit.vendorNumber,
      });
      this.isAgencyDelegate = edit.headStartId == HeadStart.HeadStartDelegate;

      this.selectedState = edit.state;
      this.selectedCodeType = edit.siteCodeType;
      this.selectedType = edit.siteType;
      this.selectedCounty = edit.county;
      this.selectedVendor = edit.vendors;
      this.selectedRegion = edit.regionIds?.split(',').map(Number);
    } else if (this.editData && this.editData.isHeadStartGrantee) {
      this.title = this.translocoService.translate('configuration.agencies.addEdit.addGranteeTitle');
      this.headStartId.setValue(HeadStart.HeadStartGrantee);
    }

    this.applyFieldPermissions();
  }

  applyFieldPermissions(): void {
    const hasPermission = this.permissionService.checkPermission(
      Permission.AccessAllAgencies
    );
    this.canEditNameAndVendor = !this.isEditMode || hasPermission;
    this.canEditAgencyStatus = hasPermission;

    if (!this.canEditNameAndVendor) {
      this.agencyName.disable();
      this.vendorNumber.disable();
    }
    if (!this.canEditAgencyStatus) {
      this.isActive.disable();
      this.isTestAgency.disable();
    }
  }

  onDropDownSelect(event: any, selectType: string) {
    switch (selectType) {
      case SelectType.States:
        this.selectedState = event;
        this.stateId.setValue(event?.id);
        this.stateId.markAsTouched();
        this.regionIds.setValue([]);
        this.countyId.reset();
        break;
      case SelectType.CodeType:
        this.codeType.setValue(event?.id);
        this.codeType.markAsTouched();
        if (this.selectedCodeType) {
          this.selectedCodeType = event;
        }
        break;
      case SelectType.SiteType:
        this.typeId.setValue(event?.id);
        this.typeId.markAsTouched();
        if (this.selectedType) {
          this.selectedType = event;
        }
        break;
      case SelectType.County:
        this.selectedCounty = event;
        this.countyId.setValue(event?.id);
        this.countyId.markAsTouched();
        break;
      case SelectType.RegionRef:
        this.selectedRegion = event;
        this.regionIds.setValue(event);
        this.regionIds.markAsTouched();
        break;
      case SelectType.HeadStartRef:
        this.headStartId.setValue(event?.id);
        this.headStartId.markAsTouched();
        break;
      case SelectType.HeadStartGranteeRef:
        this.granteeAgencyId.setValue(event?.id);
        this.granteeAgencyId.markAsTouched();
        break;
      case SelectType.StatePreschoolContractorRef:
        this.contractorAgencyId.setValue(event?.id);
        this.contractorAgencyId.markAsTouched();
        break;
      case SelectType.VendorRef:
        this.selectedVendor = event;
        this.vendorIds.setValue(
          event?.map((vendorIds: IVendor) => vendorIds.id)
        );
        this.vendorIds.markAsTouched();
        break;
      default:
        break;
    }
  }

  onSubmit(): any {
    if (this.isProcessing) return;
    if (this.agencyForm.invalid) {
      this.toast.error(this.translocoService.translate('configuration.common.requiredFieldsToast'));
      return;
    }
    // Sends Payload obj to parent
    let payload: IAgency = this.agencyForm.getRawValue();
    if (payload.headStartId != HeadStart.HeadStartDelegate)
      payload.granteeAgencyId = null;

    if (payload.statePreschoolId != StatePreschool.StatePreschoolSubcontractor)
      payload.contractorAgencyId = null;

    if (this.editData && this.editData?.data?.id) {
      payload.id = this.editData.data.id;
    }

    // Send new Agency back to table for Re-rendering
    let updateTableView: IAgency = Object.assign({}, this.agencyForm.getRawValue());
    updateTableView.agencyName = this.agencyName;
    updateTableView.state = this.selectedState;
    updateTableView.stateCode = this.selectedState?.stateCode;
    updateTableView.siteCodeType = this.selectedCodeType;
    updateTableView.siteType = this.selectedType;
    updateTableView.county = this.selectedCounty;
    updateTableView.vendors = this.selectedVendor;
    updateTableView.vendorNames = this.selectedVendor?.map(
      (vendor: any) => vendor.name
    );
    updateTableView.regionIds = this.selectedRegion;

    this.isProcessing = true;
    if (payload.id) {
      this.agencyService
        .updateAgency(payload, this.translocoService.translate('configuration.agencies.addEdit.updatedToast'))
        .pipe(take(1))
        .subscribe({
          next: (success: boolean) => {
            this.isProcessing = false;
            if (success) {
              this.dialogRef.close({
                success: true,
                updateTableView: updateTableView,
              });
            }
          },
          error: () => {
            this.isProcessing = false;
          },
        });
    } else {
      this.agencyService
        .addNewAgency(payload, this.translocoService.translate('configuration.agencies.addEdit.addedToast'))
        .pipe(take(1))
        .subscribe({
          next: (success: any) => {
            this.isProcessing = false;
            if (success) {
              this.dialogRef.close({
                success: true,
                updateTableView: updateTableView,
              });
            }
          },
          error: () => {
            this.isProcessing = false;
          },
        });
    }
  }

  onHeadStartSelect(selectedValue: IHeadStartOption): void {
    if (selectedValue?.id == HeadStart.HeadStartDelegate) {
      this.headStartId.setValue(HeadStart.HeadStartDelegate)
      this.granteeAgencyId.setValue(null);
    } else if (selectedValue?.id == HeadStart.HeadStartGrantee) {
      this.headStartId.setValue(HeadStart.HeadStartGrantee)
    }
    else
      this.headStartId.setValue(null);
  }

  cancel(): void {
    this.agencyForm.reset();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onStatePreschoolSelect(selectedValue: any): void {
    if (selectedValue?.id == StatePreschool.StatePreschoolContractor) {
      this.statePreschoolId.setValue(StatePreschool.StatePreschoolContractor)
      this.contractorAgencyId.setValue(null);
    } else if(selectedValue?.id == StatePreschool.StatePreschoolSubcontractor){
      this.statePreschoolId.setValue(StatePreschool.StatePreschoolSubcontractor)
    }
    else
      this.statePreschoolId.setValue(null);
  }
}
